/**
 * OpenPendingDefects Component (GraphQL version)
 * Displays open and pending defects for an asset using GraphQL API via services-px
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TablePagination,
	IconButton,
	Box,
	Typography,
	Button,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { Checkbox, Loading } from '@gpstrackit/gtc-react-controls';
import { getOpenAssetDefects, getClosedAssetDefects } from '../redux/slice';
import { addRepair } from '../../defectsGtc/redux/slice';
import { subDays, format } from 'date-fns';
import RepairModal from '../../defectsGtc/components/repairModal';
import PhotoGalleryModal from '../../inspectionDetails/components/photoGalleryModal';

const OpenPendingDefects = ({ assetId }) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { messages } = useIntl();
	const [page, setPage] = useState(0);
	const [perPage, setPerPage] = useState(5);
	const [expandedRows, setExpandedRows] = useState({});
	const [selectedDefects, setSelectedDefects] = useState({});
	const [repairModalOpen, setRepairModalOpen] = useState(false);
	const [currentDefectsForRepair, setCurrentDefectsForRepair] = useState([]);
	const [galleryOpen, setGalleryOpen] = useState(false);
	const [galleryPhotos, setGalleryPhotos] = useState([]);
	const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);

	const { openDefects, totalOpenDefects, loadingOpenDefects } = useSelector(
		(state) => state.assetDetailsRedux
	);
	const userInfo = useSelector((state) => state.securityRedux?.user || {});

	// Calculate dates once on mount - stable reference
	const [dates] = useState(() => [
		format(subDays(new Date(), 365), 'yyyy-MM-dd'),
		format(new Date(), 'yyyy-MM-dd'),
	]);

	const getSeverityDisplay = (severity) => {
		if (!severity) return <span>-</span>;

		// Use different font sizes for square vs triangle to make them visually similar
		const severityConfig = {
			'Safety Critical': { color: '#d32f2f', symbol: '■', fontSize: 20 },
			'Pre-Trip Critical': { color: '#f57c00', symbol: '■', fontSize: 20 },
			'Safety Advisory': { color: '#fbc02d', symbol: '▲', fontSize: 18 },
			'Maintenance Required': { color: '#1976d2', symbol: '▲', fontSize: 18 },
		};

		const config = severityConfig[severity];
		if (!config) {
			return <span>{severity}</span>;
		}

		return (
			<span
				style={{
					display: 'inline-flex',
					alignItems: 'center',
					justifyContent: 'center',
					width: 24,
					height: 24,
					color: config.color,
					fontSize: `${config.fontSize}px`,
					fontWeight: 'bold',
					lineHeight: 1,
				}}
				title={severity}
			>
				{config.symbol}
			</span>
		);
	};

	const handleInspectionClick = (inspectionId, inspectionType) => {
		if (!inspectionId) return;

		sessionStorage.setItem(
			'inspectionDetails_returnPath',
			`/app/evir-px/asset-details/${assetId}`
		);
		sessionStorage.setItem(
			'inspectionDetails_returnTitle',
			messages.assetDefectAndRepairHistory || 'Asset Defect and Repair History'
		);

		navigate(`/app/evir-px/inspection-details/${inspectionId}`, {
			state: {
				returnPath: `/app/evir-px/asset-details/${assetId}`,
				returnTitle:
					messages.assetDefectAndRepairHistory ||
					'Asset Defect and Repair History',
				inspectionType: inspectionType,
			},
		});
	};

	const toggleRowExpansion = (defectId) => {
		setExpandedRows((prev) => ({
			...prev,
			[defectId]: !prev[defectId],
		}));
	};

	const hasExpandableContent = (defect) => {
		return (defect.photos && defect.photos.length > 0) || (defect.repairs && defect.repairs.length > 0);
	};

	const handleCheckboxChange = (defectId, checked) => {
		setSelectedDefects((prev) => ({
			...prev,
			[defectId]: checked,
		}));
	};

	const getSelectedDefectsList = useCallback(() => {
		return openDefects?.filter((defect) => selectedDefects[defect.id]) || [];
	}, [openDefects, selectedDefects]);

	const hasSelectedDefects = Object.values(selectedDefects).some((v) => v);

	const handleRepairAction = () => {
		const selected = getSelectedDefectsList();
		if (selected.length === 0) return;

		// Transform all selected defects to the format expected by RepairModal
		const defectsForModal = selected.map((defect) => ({
			defectId: defect.id,
			assetId: assetId,
			inspId: defect.inspectionId,
			assetFleet: defect.reconciledAssetName || 'N/A',
			zoneName: defect.zoneLabel,
			componentName: defect.componentLabel,
			conditionName: defect.conditionLabel,
		}));
		setCurrentDefectsForRepair(defectsForModal);
		setRepairModalOpen(true);
	};

	const handleRepairSubmit = async (repairData) => {
		await dispatch(addRepair(repairData)).unwrap();

		// Clear selection and refresh both defect tables
		setSelectedDefects({});
		dispatch(
			getOpenAssetDefects({
				assetId,
				page: page + 1,
				perPage,
				dates,
			})
		);
		// Also refresh closed defects table so repaired defect appears there
		dispatch(
			getClosedAssetDefects({
				assetId,
				page: 1,
				perPage: 5,
				dates,
			})
		);
	};

	const handleRepairModalClose = () => {
		setRepairModalOpen(false);
		setCurrentDefectsForRepair([]);
	};

	const handlePhotoClick = useCallback((photos, index) => {
		if (!Array.isArray(photos) || photos.length === 0) {
			return;
		}

		const normalizedPhotos = photos.map((photo) => ({
			...photo,
			fileKey: photo.fileKey,
			url: photo.url || photo.thumbnail || photo.path,
			thumbnail: photo.thumbnail || photo.url || photo.path,
		}));

		setGalleryPhotos(normalizedPhotos);
		setSelectedPhotoIndex(typeof index === 'number' ? index : 0);
		setGalleryOpen(true);
	}, []);

	const handleCloseGallery = useCallback(() => {
		setGalleryOpen(false);
	}, []);

	useEffect(() => {
		if (!assetId) return;

		dispatch(
			getOpenAssetDefects({
				assetId,
				page: page + 1, // API uses 1-based pagination
				perPage,
				dates,
			})
		);
	}, [assetId, page, perPage, dates, dispatch]);

	// Clear selections when defects change (e.g., after page change)
	useEffect(() => {
		setSelectedDefects({});
	}, [openDefects]);

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event) => {
		setPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	if (loadingOpenDefects) {
		return (
			<Box display="flex" justifyContent="center" p={3}>
				<Loading />
			</Box>
		);
	}

	if (!openDefects || openDefects.length === 0) {
		return (
			<Box p={2}>
				<Typography variant="body2" color="textSecondary">
					{messages['asset_details_no_open_defects'] || 'No open or pending defects found'}
				</Typography>
			</Box>
		);
	}

	return (
		<Box sx={{ width: '100%' }}>
			{/* Action buttons - header is in AccordionSummary */}
			{hasSelectedDefects && (
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'flex-end',
						alignItems: 'center',
						mb: 2,
					}}
				>
					<Button
						variant="contained"
						color="primary"
						onClick={handleRepairAction}
					>
						{(messages.repairSelected || 'Repair Selected ({count})').replace('{count}', getSelectedDefectsList().length)}
					</Button>
				</Box>
			)}

			<TableContainer>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell padding="checkbox"></TableCell>
							<TableCell>{messages['defects_severity'] || 'SEVERITY'}</TableCell>
							<TableCell>{messages['defects_inspection'] || 'INSPECTION'}</TableCell>
							<TableCell>{messages['defects_zone'] || 'ZONE'}</TableCell>
							<TableCell>{messages['defects_component'] || 'COMPONENT'}</TableCell>
							<TableCell>{messages['defects_condition'] || 'CONDITION'}</TableCell>
							<TableCell>{messages['defects_last_inspection'] || 'LAST INSPECTION'}</TableCell>
							<TableCell>{messages['defects_first_inspection'] || 'FIRST INSPECTION'}</TableCell>
							<TableCell>{messages['defects_status'] || 'STATUS'}</TableCell>
							<TableCell></TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{openDefects.map((defect) => (
							<React.Fragment key={defect.id}>
								{/* Main row */}
								<TableRow
									hover
									sx={{
										backgroundColor: selectedDefects[defect.id] ? 'rgba(25, 118, 210, 0.08)' : 'inherit',
									}}
								>
									<TableCell padding="checkbox">
										<Checkbox
											checked={!!selectedDefects[defect.id]}
											onChange={(checked) => handleCheckboxChange(defect.id, checked)}
										/>
									</TableCell>
									<TableCell>{getSeverityDisplay(defect.severityLabel)}</TableCell>
									<TableCell>
										{defect.inspectionId ? (
											<span
												style={{
													color: '#1976d2',
													cursor: 'pointer',
													textDecoration: 'none',
												}}
												onMouseEnter={(e) =>
													(e.target.style.textDecoration = 'underline')
												}
												onMouseLeave={(e) =>
													(e.target.style.textDecoration = 'none')
												}
												onClick={() =>
													handleInspectionClick(
														defect.inspectionId,
														defect.inspectionType
													)
												}
											>
												{defect.inspectionType || 'View'}
											</span>
										) : (
											defect.inspectionType || '-'
										)}
									</TableCell>
									<TableCell>{defect.zoneLabel || '-'}</TableCell>
									<TableCell>{defect.componentLabel || '-'}</TableCell>
									<TableCell>{defect.conditionLabel || '-'}</TableCell>
									<TableCell>{defect.lastInspectedDate || '-'}</TableCell>
									<TableCell>{defect.firstInspectedDate || '-'}</TableCell>
									<TableCell>{defect.statusLabel || '-'}</TableCell>
									<TableCell>
										{hasExpandableContent(defect) && (
											<IconButton
												size="small"
												onClick={() => toggleRowExpansion(defect.id)}
												sx={{
													transform: expandedRows[defect.id]
														? 'rotate(180deg)'
														: 'rotate(0deg)',
													transition: 'transform 0.2s',
												}}
											>
												<ExpandMoreIcon />
											</IconButton>
										)}
									</TableCell>
								</TableRow>

								{/* Expanded row for notes/photos */}
								{expandedRows[defect.id] && hasExpandableContent(defect) && (
									<TableRow>
										<TableCell
											colSpan={10}
											sx={{ py: 2, backgroundColor: '#fafafa' }}
										>
											<Box display="flex" gap={4} flexDirection="column">
												{/* Repair details section */}
												{defect.repairs && defect.repairs.length > 0 && (
													<Box>
														<Typography
															variant="subtitle2"
															sx={{
																fontWeight: 600,
																mb: 1,
																color: '#333',
															}}
														>
															{messages.repairs || 'Repairs'}
														</Typography>
														{defect.repairs.map((repair, index) => (
															<Box
																key={index}
																display="flex"
																flexWrap="wrap"
																gap="8px 24px"
																py={1}
																sx={{
																	borderBottom: '1px solid #e0e0e0',
																	'&:last-child': { borderBottom: 'none' },
																}}
															>
																<Box sx={{ minWidth: 140 }}>
																	<Typography variant="caption" sx={{ fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>
																		{messages.repairDate || 'Repair Date'}
																	</Typography>
																	<Typography variant="body2">{repair.created || '-'}</Typography>
																</Box>
																{(repair.mechanicFirstName || repair.mechanicLastName) && (
																	<Box sx={{ minWidth: 140 }}>
																		<Typography variant="caption" sx={{ fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>
																			{messages.mechanic || 'Mechanic'}
																		</Typography>
																		<Typography variant="body2">
																			{[repair.mechanicFirstName, repair.mechanicLastName].filter(Boolean).join(' ')}
																		</Typography>
																	</Box>
																)}
																{repair.comment && (
																	<Box sx={{ minWidth: 140 }}>
																		<Typography variant="caption" sx={{ fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>
																			{messages.comment || 'Comment'}
																		</Typography>
																		<Typography variant="body2" className='wrap-text'>{repair.comment}</Typography>
																	</Box>
																)}
																{repair.resolution && (
																	<Box sx={{ minWidth: 140 }}>
																		<Typography variant="caption" sx={{ fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>
																			{messages.resolution || 'Resolution'}
																		</Typography>
																		<Typography variant="body2">{repair.resolution}</Typography>
																	</Box>
																)}
																{repair.workOrder && (
																	<Box sx={{ minWidth: 140 }}>
																		<Typography variant="caption" sx={{ fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>
																			{messages.workOrder || 'Work Order'}
																		</Typography>
																		<Typography variant="body2">{repair.workOrder}</Typography>
																	</Box>
																)}
																{repair.status && (
																	<Box sx={{ minWidth: 140 }}>
																		<Typography variant="caption" sx={{ fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>
																			{messages.status || 'Status'}
																		</Typography>
																		<Typography variant="body2">{repair.status}</Typography>
																	</Box>
																)}
															</Box>
														))}
													</Box>
												)}
												{/* Photos section */}
												{defect.photos && defect.photos.length > 0 && (
													<Box flex={1}>
														<Typography
															variant="subtitle2"
															sx={{
																fontWeight: 600,
																mb: 1,
																color: '#666',
															}}
														>
															{messages['defects_photos'] || 'DEFECT PHOTOS'}
														</Typography>
														<Box display="flex" gap={1} flexWrap="wrap">
															{defect.photos.map((photo, index) => (
																<img
																	key={photo.mediaId || index}
																	src={photo.url}
																	alt={`Defect photo ${index + 1}`}
																	style={{
																		width: 80,
																		height: 80,
																		objectFit: 'cover',
																		borderRadius: 4,
																		cursor: 'pointer',
																		border: '1px solid #ddd',
																	}}
																	role="button"
																	tabIndex={0}
																	onClick={() => handlePhotoClick(defect.photos, index)}
																	onKeyDown={(event) => {
																		if (event.key === 'Enter' || event.key === ' ') {
																			event.preventDefault();
																			handlePhotoClick(defect.photos, index);
																		}
																	}}
																/>
															))}
														</Box>
													</Box>
												)}
											</Box>
										</TableCell>
									</TableRow>
								)}
							</React.Fragment>
						))}
					</TableBody>
				</Table>
			</TableContainer>

			{totalOpenDefects > 5 && (
				<TablePagination
					component="div"
					count={totalOpenDefects}
					page={page}
					onPageChange={handleChangePage}
					rowsPerPage={perPage}
					onRowsPerPageChange={handleChangeRowsPerPage}
					rowsPerPageOptions={[5, 10, 25]}
				/>
			)}

			{/* Repair Modal */}
			<RepairModal
				open={repairModalOpen}
				onClose={handleRepairModalClose}
				onSubmit={handleRepairSubmit}
				defects={currentDefectsForRepair}
				userInfo={userInfo}
			/>
			<PhotoGalleryModal
				open={galleryOpen}
				onClose={handleCloseGallery}
				photos={galleryPhotos}
				initialIndex={selectedPhotoIndex}
			/>
		</Box>
	);
};

export default OpenPendingDefects;
